import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import { UploadCloud, FileText, X, Loader2, RefreshCw, Download } from "lucide-react";
import CustomFormField, { getFieldIds } from "../CustomFormField/CustomFormField";
import { safeHref } from "../utils/safeUrl";
import "../CustomTheme/customTheme.css";
import "./CustomFileUpload.css";

const IMAGE_EXT = /\.(png|jpe?g|gif|webp|svg|bmp|avif)(\?.*)?$/i;

const formatSize = (bytes) =>
  bytes >= 1024 * 1024
    ? `${(bytes / (1024 * 1024)).toFixed(1)} MB`
    : `${Math.max(1, Math.round(bytes / 1024))} KB`;

// "report%20one.pdf" → "report one.pdf"; a badly encoded name is shown as it is instead of crashing
const fileNameFromUrl = (url) => {
  const last = url.split("/").pop().split("?")[0];
  try {
    return decodeURIComponent(last);
  } catch (_) {
    return last;
  }
};

// 4.8828125 → "4.9", 5 → "5"
const formatLimit = (mb) => String(Number(Number(mb).toFixed(1)));

// the MIME types a browser may report for each extension we know
const EXT_MIME = {
  png: ["image/png", "image/x-png"],
  jpg: ["image/jpeg", "image/pjpeg"],
  jpeg: ["image/jpeg", "image/pjpeg"],
  jfif: ["image/jpeg", "image/pjpeg"], // Windows sometimes saves JPEG photos as .jfif
  gif: ["image/gif"],
  webp: ["image/webp"],
  bmp: ["image/bmp", "image/x-ms-bmp"],
  avif: ["image/avif"],
  heic: ["image/heic"],
  heif: ["image/heif"],
  svg: ["image/svg+xml"],
  pdf: ["application/pdf"],
};
// what "image/*" means here: raster images only — SVG can carry <script>, so it must be asked for (".svg")
const RASTER_EXT = ["png", "jpg", "jpeg", "jfif", "gif", "webp", "bmp", "avif", "heic", "heif"];

const extOf = (name) => {
  const m = String(name || "").toLowerCase().match(/\.([a-z0-9]+)$/);
  return m ? m[1] : "";
};

const matchesAccept = (file, accept) => {
  if (!accept) return true;
  const name = file.name.toLowerCase();
  const ext = extOf(name);
  const mime = (file.type || "").toLowerCase();
  return accept
    .split(",")
    .map((a) => a.trim().toLowerCase())
    .filter(Boolean)
    .some((rule) => {
      if (rule.startsWith(".")) return name.endsWith(rule);
      if (rule === "image/*") return mime.startsWith("image/") && RASTER_EXT.includes(ext);
      if (rule.endsWith("/*")) return mime.startsWith(rule.slice(0, -1));
      return mime === rule;
    });
};

/**
 * Validates one file against `accept` and `maxSizeMB`, and that its extension matches
 * the type the browser reports (a renamed "photo.html" can't pass as an image).
 * Returns an error message string, or "" when the file is fine.
 * Exported so screens can reuse the same rules without the component.
 * Client checks can be bypassed — the server must validate uploads too.
 */
export const validateFile = (file, { accept, maxSizeMB } = {}) => {
  if (!file) return "";
  if (!matchesAccept(file, accept)) {
    return `This file type is not allowed. Allowed: ${accept}`;
  }
  const ext = extOf(file.name);
  const mime = (file.type || "").toLowerCase();
  if (EXT_MIME[ext] && mime && !EXT_MIME[ext].includes(mime)) {
    return `The file's extension (.${ext}) doesn't match its type (${mime}).`;
  }
  if (maxSizeMB && file.size > maxSizeMB * 1024 * 1024) {
    return `File is too large (${formatSize(file.size)}). Maximum size is ${formatLimit(maxSizeMB)} MB.`;
  }
  return "";
};

/* first bytes ("magic numbers") of the formats we can recognise */
const SIGNATURES = {
  png: { label: "PNG", test: (b) => [0x89, 0x50, 0x4e, 0x47, 0x0d, 0x0a, 0x1a, 0x0a].every((x, i) => b[i] === x) },
  jpg: { label: "JPEG", test: (b) => b[0] === 0xff && b[1] === 0xd8 && b[2] === 0xff },
  gif: { label: "GIF", test: (b) => String.fromCharCode(...b.slice(0, 4)) === "GIF8" },
  webp: { label: "WEBP", test: (b) => String.fromCharCode(...b.slice(0, 4)) === "RIFF" && String.fromCharCode(...b.slice(8, 12)) === "WEBP" },
  bmp: { label: "BMP", test: (b) => b[0] === 0x42 && b[1] === 0x4d },
  pdf: { label: "PDF", test: (b) => String.fromCharCode(...b.slice(0, 5)) === "%PDF-" },
};
SIGNATURES.jpeg = SIGNATURES.jpg;
SIGNATURES.jfif = SIGNATURES.jpg;

const readHead = (file, length) => {
  const blob = file.slice(0, length);
  if (typeof blob.arrayBuffer === "function") return blob.arrayBuffer().then((buf) => new Uint8Array(buf));
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(new Uint8Array(reader.result));
    reader.onerror = () => reject(reader.error);
    reader.readAsArrayBuffer(blob);
  });
};

/**
 * Checks that the file's content really is what its extension says (PNG, JPEG, GIF,
 * WEBP, BMP, PDF). Resolves to an error message, or "" (also for formats it can't check).
 */
export const validateFileContent = async (file) => {
  const sig = SIGNATURES[extOf(file?.name)];
  if (!sig) return "";
  try {
    const head = await readHead(file, 12);
    return sig.test(head) ? "" : `This file isn't a real ${sig.label} (its content doesn't match .${extOf(file.name)}).`;
  } catch (_) {
    return "Couldn't read this file. Please choose it again.";
  }
};

/**
 * CustomFileUpload — drag & drop / click-to-browse file picker.
 *
 * Props:
 *  - accept      e.g. "image/*" or ".pdf,.docx" (validated, not just a hint)
 *  - maxSizeMB   size limit (default 1)
 *  - value       current file: a File, or a URL string of an already-uploaded file
 *  - onChange(file | null)   called with a VALID file, or null when removed
 *  - onUpload(file)          optional; return a Promise to show the uploading state
 *  - uploading   controlled "uploading" flag (alternative to onUpload's Promise)
 *  - variant     "dropzone" (default, large box) | "compact" (single row)
 *  - downloadable  show a Download button for a saved file (value = URL), even when disabled
 *  - label | title, name, id, isRequired, hint, error, colClass, disabled
 */
const CustomFileUpload = ({
  label,
  title,
  name,
  id,
  isRequired,
  hint,
  error,
  colClass,
  accept,
  maxSizeMB = 1,
  value,
  onChange,
  onUpload,
  uploading: uploadingProp,
  variant = "dropzone",
  disabled = false,
  downloadable = false,
}) => {
  const autoId = useId();
  const inputId = id || name || `cfu-${autoId}`;
  const ids = getFieldIds(inputId);
  const inputRef = useRef(null);

  const [localError, setLocalError] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadingLocal, setUploadingLocal] = useState(false);

  const uploading = uploadingProp ?? uploadingLocal;
  const shownError = error || localError;

  /* ---------- preview for a File (object URL) or an existing URL ---------- */
  const isFile = typeof File !== "undefined" && value instanceof File;
  const objectUrl = useMemo(() => (isFile ? URL.createObjectURL(value) : ""), [isFile, value]);
  useEffect(() => () => objectUrl && URL.revokeObjectURL(objectUrl), [objectUrl]);

  const fileName = isFile
    ? value.name
    : typeof value === "string" && value
      ? fileNameFromUrl(value)
      : "";
  // a saved file's URL comes from the API — never put a javascript:/data: URL in href/src
  const previewUrl = isFile ? objectUrl : typeof value === "string" ? safeHref(value) || "" : "";
  const isImage = isFile ? value.type.startsWith("image/") : IMAGE_EXT.test(previewUrl);
  const hasFile = Boolean(fileName);

  const hintText =
    hint ??
    [accept ? `Allowed: ${accept}` : null, maxSizeMB ? `Max ${formatLimit(maxSizeMB)} MB` : null]
      .filter(Boolean)
      .join(" · ");

  const pick = () => {
    if (!disabled && !uploading) inputRef.current?.click();
  };

  const handleFile = async (file) => {
    if (!file) return;
    const message = validateFile(file, { accept, maxSizeMB }) || (await validateFileContent(file));
    if (message) {
      setLocalError(message);
      return;
    }
    setLocalError("");
    onChange?.(file);
    if (onUpload) {
      setUploadingLocal(true);
      try {
        await onUpload(file);
      } catch (err) {
        setLocalError(err?.msg || err?.message || "Upload failed. Please try again.");
      } finally {
        setUploadingLocal(false);
      }
    }
  };

  const onInputChange = (e) => {
    handleFile(e.target.files?.[0]);
    e.target.value = ""; // allow choosing the same file again
  };

  const onDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    if (disabled || uploading) return;
    handleFile(e.dataTransfer.files?.[0]);
  };

  const remove = (e) => {
    e.stopPropagation();
    setLocalError("");
    onChange?.(null);
  };

  const zoneClass = `cfu-zone cfu-${variant}${dragOver ? " cfu-dragOver" : ""}${
    shownError ? " cfu-error" : ""
  }${disabled ? " cfu-disabled" : ""}${hasFile ? " cfu-hasFile" : ""}`;

  return (
    <CustomFormField
      label={label ?? title}
      isRequired={isRequired}
      htmlFor={inputId}
      hint={hintText}
      error={shownError}
      colClass={colClass}
    >
      <input
        ref={inputRef}
        id={inputId}
        name={name}
        type="file"
        accept={accept}
        disabled={disabled}
        onChange={onInputChange}
        className="cui-srOnly"
        aria-invalid={Boolean(shownError)}
        aria-describedby={ids.describedBy(shownError, hintText)}
      />

      <div
        className={zoneClass}
        role="button"
        tabIndex={disabled ? -1 : 0}
        aria-disabled={disabled}
        onClick={pick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            pick();
          }
        }}
        onDragOver={(e) => {
          e.preventDefault();
          if (!disabled) setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={onDrop}
      >
        {hasFile ? (
          <div className="cfu-file">
            {isImage && previewUrl ? (
              <img src={previewUrl} alt="" className="cfu-thumb" />
            ) : (
              <span className="cfu-fileIcon">
                <FileText size={20} />
              </span>
            )}

            <div className="cfu-fileMeta">
              <span className="cfu-fileName" title={fileName}>
                {fileName}
              </span>
              <span className="cfu-fileSub">
                {uploading
                  ? "Uploading…"
                  : isFile
                    ? formatSize(value.size)
                    : disabled
                      ? "Saved file"
                      : "Click to replace"}
              </span>
            </div>

            <div className="cfu-fileActions">
              {/* saved file (URL) → download, also when the field is read-only */}
              {downloadable && !isFile && previewUrl && !uploading && (
                <a
                  className="cfu-iconBtn"
                  href={previewUrl}
                  download
                  target="_blank"
                  rel="noopener noreferrer"
                  title="Download"
                  aria-label="Download file"
                  onClick={(e) => e.stopPropagation()}
                >
                  <Download size={15} />
                </a>
              )}
              {uploading ? (
                <Loader2 size={18} className="cfu-spin" />
              ) : (
                !disabled && (
                  <>
                    <span className="cfu-iconBtn" title="Replace" aria-hidden="true">
                      <RefreshCw size={15} />
                    </span>
                    <button
                      type="button"
                      className="cfu-iconBtn cfu-removeBtn"
                      onClick={remove}
                      aria-label="Remove file"
                      title="Remove"
                    >
                      <X size={15} />
                    </button>
                  </>
                )
              )}
            </div>
          </div>
        ) : (
          <div className="cfu-empty">
            <span className="cfu-uploadIcon">
              {uploading ? <Loader2 size={20} className="cfu-spin" /> : <UploadCloud size={20} />}
            </span>
            <span className="cfu-emptyText">
              <strong>Click to upload</strong>
              {variant === "dropzone" && <span> or drag and drop</span>}
            </span>
          </div>
        )}
      </div>
    </CustomFormField>
  );
};

export default CustomFileUpload;
